import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import ModalForm from '@/common/components/modal-form';
import style from './style.module.scss';
const ENTITY_LABELS = {
    Budget: 'Бюджет',
    Category: 'Категория',
    Transaction: 'Операция',
};
function conflictKey(c) {
    return `${c.entityType}:${c.entityId}`;
}
/** Короткая человекочитаемая сводка версии для сравнения в панели. */
function summarize(entityType, payload) {
    if (!payload)
        return '—';
    switch (entityType) {
        case 'Budget':
            return String(payload.name ?? '(без названия)');
        case 'Category':
            return String(payload.label ?? '(без названия)');
        case 'Transaction': {
            const parts = [];
            if (payload.date)
                parts.push(String(payload.date));
            const amount = payload.actualAmount ?? payload.plannedAmount;
            if (amount != null)
                parts.push(String(amount));
            if (payload.description)
                parts.push(String(payload.description));
            return parts.join(' · ') || '(операция)';
        }
        default:
            return JSON.stringify(payload);
    }
}
export default function ConflictResolutionPanel({ onClose, budgetId, fetchConflicts, resolveConflict, onResolved, }) {
    const [conflicts, setConflicts] = useState(null);
    const [choices, setChoices] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        let alive = true;
        fetchConflicts()
            .then((all) => {
            if (!alive)
                return;
            // Только конфликты текущего бюджета (индикатор per-budget).
            const list = budgetId ? all.filter((c) => c.budgetId === budgetId) : all;
            // Защитный дедуп по сущности (на случай, если сервер вернул дубликаты конфликтов):
            // одинаковые entityType:entityId с общим radio-name иначе снимают выбор друг у друга.
            const seen = new Set();
            const unique = list.filter((c) => {
                const key = conflictKey(c);
                if (seen.has(key))
                    return false;
                seen.add(key);
                return true;
            });
            setConflicts(unique);
            // По умолчанию — серверная версия (сервер authority): безопасный default.
            const init = {};
            for (const c of unique)
                init[conflictKey(c)] = 'KeepServer';
            setChoices(init);
        })
            .catch(() => {
            if (alive)
                setError('Не удалось загрузить конфликты.');
        });
        return () => {
            alive = false;
        };
    }, [fetchConflicts, budgetId]);
    const setChoice = (key, choice) => {
        setChoices((s) => ({ ...s, [key]: choice }));
    };
    const apply = async () => {
        if (!conflicts || conflicts.length === 0) {
            // Сервер конфликтов не видит, но локально мог залипнуть счётчик — прогоняем
            // sync (внутри reconcileConflicts почистит осиротевшие записи), затем закрываем.
            setBusy(true);
            try {
                await onResolved();
            }
            catch {
                // не критично — реконсиляция повторится на следующем sync
            }
            onClose();
            return;
        }
        setBusy(true);
        setError(null);
        try {
            for (const c of conflicts) {
                await resolveConflict(c.entityType, c.entityId, choices[conflictKey(c)] ?? 'KeepServer');
            }
            // Подтянуть результат (KeepMine новую версию) и пересчитать счётчики.
            await onResolved();
            onClose();
        }
        catch {
            setError('Не удалось разрешить часть конфликтов. Попробуйте ещё раз.');
            setBusy(false);
        }
    };
    const isEmpty = conflicts !== null && conflicts.length === 0;
    return (_jsxs(ModalForm, { headlineText: "\u041A\u043E\u043D\u0444\u043B\u0438\u043A\u0442\u044B \u0441\u0438\u043D\u0445\u0440\u043E\u043D\u0438\u0437\u0430\u0446\u0438\u0438", submitButtonText: isEmpty ? 'Закрыть' : 'Применить выбор', submitButtonDisabled: busy || conflicts === null, onClose: onClose, onSubmit: apply, isWide: true, children: [conflicts === null && _jsx("p", { className: style['hint'], children: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430\u2026" }), isEmpty && _jsx("p", { className: style['hint'], children: "\u041A\u043E\u043D\u0444\u043B\u0438\u043A\u0442\u043E\u0432 \u043D\u0435\u0442." }), error && _jsx("p", { className: style['error'], children: error }), conflicts && conflicts.length > 0 && (_jsxs(_Fragment, { children: [_jsx("p", { className: style['intro'], children: "\u042D\u0442\u0438 \u0437\u0430\u043F\u0438\u0441\u0438 \u0431\u044B\u043B\u0438 \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u044B \u043D\u0430 \u0434\u0440\u0443\u0433\u043E\u043C \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u0435. \u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435, \u043A\u0430\u043A\u0443\u044E \u0432\u0435\u0440\u0441\u0438\u044E \u043E\u0441\u0442\u0430\u0432\u0438\u0442\u044C." }), conflicts.map((c) => {
                        const key = conflictKey(c);
                        const choice = choices[key] ?? 'KeepServer';
                        return (_jsxs("div", { className: style['conflict'], children: [_jsx("div", { className: style['conflict-title'], children: ENTITY_LABELS[c.entityType] }), _jsxs("div", { className: style['versions'], children: [_jsxs("label", { className: [style['version'], choice === 'KeepMine' ? style['selected'] : '']
                                                .filter(Boolean)
                                                .join(' '), children: [_jsx("input", { type: "radio", name: key, checked: choice === 'KeepMine', disabled: busy, onChange: () => setChoice(key, 'KeepMine') }), _jsx("span", { className: style['version-tag'], children: "\u041C\u043E\u044F \u0432\u0435\u0440\u0441\u0438\u044F" }), _jsx("span", { className: style['summary'], children: summarize(c.entityType, c.localPayload) })] }), _jsxs("label", { className: [style['version'], choice === 'KeepServer' ? style['selected'] : '']
                                                .filter(Boolean)
                                                .join(' '), children: [_jsx("input", { type: "radio", name: key, checked: choice === 'KeepServer', disabled: busy, onChange: () => setChoice(key, 'KeepServer') }), _jsx("span", { className: style['version-tag'], children: "\u0421\u0435\u0440\u0432\u0435\u0440\u043D\u0430\u044F" }), _jsx("span", { className: style['summary'], children: c.remotePayload === null
                                                        ? 'Удалено на сервере'
                                                        : summarize(c.entityType, c.remotePayload) })] })] })] }, key));
                    })] }))] }));
}
